/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2020 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

import { getUrlWithoutSearchParams } from "bundle/util/UriUtils";
import { getCurrentEnv } from "bundle/util/EnvUtil";
import { isValidAllowedURL } from "common/util/EnvUtil";

const ACROBAT_PLAN_ENV_CONFIG = {
    local: {
        acrobatPlanUrls: {
            default: "https://www-stage01.acrobat.adobe.com/{territory}/{lang}/acrobat/pricing.html",
        },
    },
    dev: {
        acrobatPlanUrls: {
            default: "https://www-stage01.acrobat.adobe.com/{territory}/{lang}/acrobat/pricing.html",
        },
    },
    stage: {
        acrobatPlanUrls: {
            default: "https://www-stage01.acrobat.adobe.com/{territory}/{lang}/acrobat/pricing.html",
        },
    },
    prod: {
        acrobatPlanUrls: {
            default: "https://acrobat.adobe.com/{territory}/{lang}/acrobat/pricing.html",
        },
    },
};

const INTERNAL_ENV_CONFIG = {
    local: {
        dcapiUri: "https://dc-api-stage.adobe.io/discovery",
        // eslint-disable-next-line max-len
        dcapiAccept: "application/vnd.adobe.dc+json;profile=\"https://dc-api-stage.adobe.io/schemas/discovery_v1.json\"",
        floodgateUri: "https://p13n-stage.adobe.io/fg/api",
        floodgateApiKey: "dc-local-virgoweb",
        loggingUri: "https://dc-api-dev.adobe.io",
        licenseUri: "",
        internalLogToConsoleEnabled: false,
        internalLogToServerEnabled: false,
        floodgateEnabled: true,
        defaultNoRestriction: true,
        viewSDKAppVersion: "__VERSION__",
        sdkDocumentationUrl: "https://www.adobe.com/go/dcviewsdk_docs",
        documentDomain: "acrobat.com",
        brandingUrl: "https://www.stage.adobe.com/acrobat/online.html",
        otDomainId: "7a5eb705-95ed-4cc4-a11d-0cc5760e93db-test",
        floodgateUseAnonymousUUID: false,
    },
    dev: {
        dcapiUri: "https://dc-api-stage.adobe.io/discovery",
        // eslint-disable-next-line max-len
        dcapiAccept: "application/vnd.adobe.dc+json;profile=\"https://dc-api-stage.adobe.io/schemas/discovery_v1.json\"",
        floodgateUri: "https://p13n-stage.adobe.io/fg/api",
        floodgateApiKey: "dc-dev-virgoweb",
        loggingUri: "https://dc-api-dev.adobe.io",
        licenseUri: "https://viewlicense-dev.adobe.io/viewsdklicense/jwt",
        internalLogToConsoleEnabled: false,
        internalLogToServerEnabled: true,
        floodgateEnabled: true,
        defaultNoRestriction: false,
        viewSDKAppVersion: "__VERSION__",
        sdkDocumentationUrl: "https://www.stage.adobe.io/apis/documentcloud/dcsdk/docs.html",
        documentDomain: "adobe.com",
        brandingUrl: "https://www.stage.adobe.com/acrobat/online.html",
        otDomainId: "7a5eb705-95ed-4cc4-a11d-0cc5760e93db-test",
        floodgateUseAnonymousUUID: false,
    },
    stage: {
        dcapiUri: "https://dc-api-stage.adobe.io/discovery",
        // eslint-disable-next-line max-len
        dcapiAccept: "application/vnd.adobe.dc+json;profile=\"https://dc-api-stage.adobe.io/schemas/discovery_v1.json\"",
        floodgateUri: "https://p13n-stage.adobe.io/fg/api",
        floodgateApiKey: "dc-stage-virgoweb",
        loggingUri: "https://dc-api-stage.adobe.io",
        licenseUri: "https://viewlicense-stage.adobe.io/viewsdklicense/jwt",
        internalLogToConsoleEnabled: false,
        internalLogToServerEnabled: true,
        floodgateEnabled: true,
        defaultNoRestriction: false,
        viewSDKAppVersion: "__VERSION__",
        sdkDocumentationUrl: "https://www.stage.adobe.io/apis/documentcloud/dcsdk/docs.html",
        documentDomain: "adobe.com",
        brandingUrl: "https://www.stage.adobe.com/acrobat/online.html",
        otDomainId: "7a5eb705-95ed-4cc4-a11d-0cc5760e93db-test",
        floodgateUseAnonymousUUID: false,
    },
    prod: {
        dcapiUri: "https://dc-api.adobe.io/discovery",
        // eslint-disable-next-line max-len
        dcapiAccept: "application/vnd.adobe.dc+json;profile=\"https://dc-api.adobe.io/schemas/discovery_v1.json\"",
        floodgateUri: "https://p13n.adobe.io/fg/api",
        floodgateApiKey: "dc-prod-virgoweb",
        loggingUri: "https://dc-api.adobe.io",
        licenseUri: "https://viewlicense.adobe.io/viewsdklicense/jwt",
        internalLogToConsoleEnabled: false,
        internalLogToServerEnabled: true,
        floodgateEnabled: true,
        defaultNoRestriction: false,
        viewSDKAppVersion: "__VERSION__",
        sdkDocumentationUrl: "https://www.adobe.com/go/dcviewsdk_docs",
        documentDomain: "adobe.com",
        brandingUrl: "https://www.adobe.com/go/dcpdfembedapibranding",
        otDomainId: "7a5eb705-95ed-4cc4-a11d-0cc5760e93db",
        floodgateUseAnonymousUUID: false,
    },
};

const ALLOWED_DOMAINS_FOR_AA = [
    ".acrobat.com",
    "documentcloud.adobe.com",
    "acrobat.adobe.com",
    "documentservices.adobe.com",
    "acrobatservices.adobe.com",
];

const getNoRestriction = config => {
    if (config.defaultNoRestriction) {
        return true;
    }

    return isValidAllowedURL(config.eventOrigin);
};

const getCMPFlags = config => ({
    disableCMPTool: true,
    disableMeasurePerformance: !isValidAllowedURL(config.eventOrigin, ALLOWED_DOMAINS_FOR_AA),
});

const getFinalConfig = config => {
    let finalConfig = { ...config, serverEnv: getCurrentEnv(getUrlWithoutSearchParams(window.location.href)) };
    finalConfig = { ...finalConfig, ...INTERNAL_ENV_CONFIG[finalConfig.serverEnv] };
    finalConfig = { ...ACROBAT_PLAN_ENV_CONFIG[finalConfig.serverEnv], ...finalConfig };
    finalConfig.noRestriction = getNoRestriction(finalConfig);
    return { ...finalConfig, ...getCMPFlags(finalConfig) };
};

export default getFinalConfig;
